import React, { useState, useRef } from 'react';
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, MailCheck, MailX } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { importUsers, downloadUsersTemplate } from '../services/api';
import { toast } from 'sonner';

const StatPill = ({ icon: Icon, label, value, colorClass, testId }) => (
  <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${colorClass}`} data-testid={testId}>
    <Icon className="w-4 h-4" />
    <span className="text-sm font-medium">{label}:</span>
    <span className="text-sm font-bold">{value}</span>
  </div>
);

const BulkImportUsersDialog = ({ open, onOpenChange, onImported }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = (val) => {
    if (!val) reset();
    onOpenChange(val);
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadUsersTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_usuarios.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erro ao baixar o modelo');
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Selecione um arquivo .xlsx ou .csv');
      return;
    }
    setImporting(true);
    setResult(null);
    try {
      const res = await importUsers(file);
      setResult(res.data);
      const c = res.data.created_count;
      if (c > 0) {
        toast.success(`${c} usuário(s) criado(s) com sucesso`);
        onImported?.();
      } else {
        toast.info('Nenhum usuário novo foi criado');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao importar usuários');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadCredentials = () => {
    if (!result?.created?.length) return;
    const roleLabels = { admin: 'Administrador', gestor: 'Gestor', usuario: 'Usuário' };
    const header = ['Nome', 'Email', 'Perfil', 'Senha', 'Email enviado'];
    const lines = result.created.map((u) => [
      u.name,
      u.email,
      roleLabels[u.role] || u.role,
      u.password,
      u.email_sent ? 'Sim' : 'Não',
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';'));
    const csv = '\ufeff' + [header.join(';'), ...lines].join('\r\n');
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `credenciais_usuarios_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-white border-gray-200 sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-import-users-dialog">
        <DialogHeader>
          <DialogTitle className="text-gray-900 flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#003366]" />
            Importar usuários em massa
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Envie uma planilha Excel/CSV com as colunas <strong>Nome, Email e Perfil</strong>. A senha de cada usuário é gerada automaticamente e enviada por e-mail. E-mails já cadastrados são ignorados.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">Baixe o modelo</p>
                  <p className="text-xs text-gray-500">Perfis válidos: admin, gestor, usuario</p>
                </div>
              </div>
              <Button variant="outline" onClick={handleDownloadTemplate} className="bg-white border-gray-300 text-gray-700" data-testid="download-users-template-btn">
                <Download className="w-4 h-4 mr-2" />
                Baixar modelo
              </Button>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Arquivo (.xlsx ou .csv)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-[#003366] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-[#002244] cursor-pointer border border-gray-300 rounded-lg"
                data-testid="bulk-import-file-input"
              />
              {file && <p className="text-xs text-gray-500">Selecionado: {file.name}</p>}
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2" data-testid="bulk-import-result">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatPill icon={CheckCircle2} label="Criados" value={result.created_count} colorClass="bg-emerald-50 border-emerald-200 text-emerald-700" testId="result-created" />
              <StatPill icon={MailCheck} label="E-mails" value={result.emails_sent} colorClass="bg-blue-50 border-blue-200 text-[#003366]" testId="result-emails" />
              <StatPill icon={AlertTriangle} label="Pulados" value={result.skipped_count} colorClass="bg-amber-50 border-amber-200 text-amber-700" testId="result-skipped" />
              <StatPill icon={XCircle} label="Erros" value={result.error_count} colorClass="bg-rose-50 border-rose-200 text-rose-700" testId="result-errors" />
            </div>

            {result.created?.length > 0 && (
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <p className="text-sm font-semibold text-gray-800">Usuários criados ({result.created.length})</p>
                  <Button size="sm" variant="outline" onClick={handleDownloadCredentials} className="bg-white border-gray-300 text-gray-700" data-testid="download-credentials-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Baixar credenciais (CSV)
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                  {result.created.map((u) => (
                    <div key={u.email} className="flex items-center justify-between px-4 py-2 text-sm">
                      <div>
                        <p className="font-medium text-gray-800">{u.name}</p>
                        <p className="text-xs text-gray-500">{u.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono text-gray-700">{u.password}</code>
                        {u.email_sent
                          ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs"><MailCheck className="w-3 h-3 mr-1" />Enviado</Badge>
                          : <Badge variant="outline" className="bg-gray-100 text-gray-500 border-gray-300 text-xs"><MailX className="w-3 h-3 mr-1" />Não enviado</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.skipped?.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-semibold text-amber-800 mb-1">Pulados</p>
                <ul className="text-xs text-amber-700 space-y-0.5 max-h-24 overflow-y-auto">
                  {result.skipped.map((s, i) => (
                    <li key={i}>Linha {s.row_num}: {s.email} — {s.reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                <p className="text-sm font-semibold text-rose-800 mb-1">Erros</p>
                <ul className="text-xs text-rose-700 space-y-0.5 max-h-24 overflow-y-auto">
                  {result.errors.map((e, i) => (<li key={i}>{e}</li>))}
                </ul>
              </div>
            )}

            <p className="text-xs text-gray-500">
              Guarde o arquivo de credenciais em local seguro. As senhas não poderão ser recuperadas depois — use "Redefinir senha" caso necessário.
            </p>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => handleClose(false)} className="bg-white border-gray-300 text-gray-700">Cancelar</Button>
              <Button onClick={handleImport} disabled={importing || !file} className="bg-[#003366] hover:bg-[#002244]" data-testid="submit-bulk-import-btn">
                {importing ? 'Importando...' : 'Importar'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={reset} className="bg-white border-gray-300 text-gray-700" data-testid="import-again-btn">Importar outro arquivo</Button>
              <Button onClick={() => handleClose(false)} className="bg-[#003366] hover:bg-[#002244]" data-testid="close-bulk-import-btn">Concluir</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportUsersDialog;
